const API_BASE = '/api/v1/profile';

export interface UserProfile {
  id: number;
  userId: string;
  dateOfBirth: string | null;
  sex: string | null;
  height: number | null;
  weight: number | null;
  bloodType: string | null;
  allergies: string | null;
  chronicConditions: string | null;
  familyHistory: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  // Calculated fields
  bmi?: number | null;
  age?: number | null;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'MEMBER';
  createdAt: string;
  lastLoginAt: string | null;
  profile: UserProfile | null;
}

export interface ProfileStats {
  documents: number;
  parameters: number;
  uniqueParameters: number;
  conversations: number;
  outOfRange: number;
  latestTestDate: string | null;
}

export interface ProfileUpdateData {
  name?: string;
  dateOfBirth?: string | null;
  sex?: string | null;
  height?: number | null;
  weight?: number | null;
  bloodType?: string | null;
  allergies?: string | null;
  chronicConditions?: string | null;
  familyHistory?: Record<string, unknown> | null;
}

export async function getProfile(): Promise<{ success: boolean; user: User }> {
  const response = await fetch(API_BASE, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch profile');
  }

  return response.json();
}

export async function updateProfile(
  data: ProfileUpdateData
): Promise<{ success: boolean; user: User }> {
  const response = await fetch(API_BASE, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to update profile');
  }

  return response.json();
}

export async function getProfileStats(): Promise<{ success: boolean; stats: ProfileStats }> {
  const response = await fetch(`${API_BASE}/stats`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch profile stats');
  }

  return response.json();
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/password`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to change password');
  }

  return data;
}

export async function deleteAccount(): Promise<{ success: boolean; message: string }> {
  const response = await fetch(API_BASE, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete account');
  }

  return response.json();
}

// Utility functions
export function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getBmiCategory(bmi: number): { label: string; color: string } {
  if (bmi < 18.5) return { label: 'Sottopeso', color: 'text-yellow-600' };
  if (bmi < 25) return { label: 'Normopeso', color: 'text-green-600' };
  if (bmi < 30) return { label: 'Sovrappeso', color: 'text-orange-600' };
  return { label: 'Obesità', color: 'text-red-600' };
}

export const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', '0+', '0-'];
export const sexOptions = [
  { value: 'M', label: 'Maschio' },
  { value: 'F', label: 'Femmina' },
];

// ==================== WEIGHT MEASUREMENTS ====================

export interface WeightMeasurement {
  id: number;
  userId: string;
  weight: number;
  date: string;
  notes: string | null;
  createdAt: string;
}

export async function getWeightMeasurements(limit?: number): Promise<{ success: boolean; measurements: WeightMeasurement[] }> {
  const params = limit ? `?limit=${limit}` : '';
  const response = await fetch(`${API_BASE}/measurements/weight${params}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento delle misurazioni');
  return response.json();
}

export async function createWeightMeasurement(data: { weight: number; date: string; notes?: string }): Promise<{ success: boolean; measurement: WeightMeasurement }> {
  const response = await fetch(`${API_BASE}/measurements/weight`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nella registrazione del peso');
  return response.json();
}

export async function deleteWeightMeasurement(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/measurements/weight/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}

// ==================== BLOOD PRESSURE MEASUREMENTS ====================

export interface BloodPressureMeasurement {
  id: number;
  userId: string;
  systolic: number;
  diastolic: number;
  heartRate: number | null;
  date: string;
  notes: string | null;
  createdAt: string;
}

export async function getBloodPressureMeasurements(limit?: number): Promise<{ success: boolean; measurements: BloodPressureMeasurement[] }> {
  const params = limit ? `?limit=${limit}` : '';
  const response = await fetch(`${API_BASE}/measurements/blood-pressure${params}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento delle misurazioni');
  return response.json();
}

export async function createBloodPressureMeasurement(data: { systolic: number; diastolic: number; heartRate?: number; date: string; notes?: string }): Promise<{ success: boolean; measurement: BloodPressureMeasurement }> {
  const response = await fetch(`${API_BASE}/measurements/blood-pressure`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nella registrazione della pressione');
  return response.json();
}

export async function deleteBloodPressureMeasurement(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/measurements/blood-pressure/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}

// ==================== FAMILY MEMBERS ====================

export interface FamilyMember {
  id: number;
  userId: string;
  relation: string;
  birthYear: number | null;
  conditions: string[];
  diagnosisAge: number | null;
  notes: string | null;
  createdAt: string;
}

export const familyRelations = [
  { value: 'padre', label: 'Padre' },
  { value: 'madre', label: 'Madre' },
  { value: 'fratello', label: 'Fratello' },
  { value: 'sorella', label: 'Sorella' },
  { value: 'nonno_paterno', label: 'Nonno paterno' },
  { value: 'nonna_paterna', label: 'Nonna paterna' },
  { value: 'nonno_materno', label: 'Nonno materno' },
  { value: 'nonna_materna', label: 'Nonna materna' },
  { value: 'zio', label: 'Zio' },
  { value: 'zia', label: 'Zia' },
  { value: 'figlio', label: 'Figlio' },
  { value: 'figlia', label: 'Figlia' },
];

export const commonConditions = [
  'Tumore alla prostata',
  'Tumore al seno',
  'Tumore al colon',
  'Tumore ai polmoni',
  'Diabete tipo 2',
  'Diabete tipo 1',
  'Ipertensione',
  'Malattie cardiovascolari',
  'Infarto',
  'Ictus',
  'Alzheimer',
  'Parkinson',
  'Artrite reumatoide',
  'Osteoporosi',
  'Asma',
  'Celiachia',
];

export async function getFamilyMembers(): Promise<{ success: boolean; members: FamilyMember[] }> {
  const response = await fetch(`${API_BASE}/family`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento della storia familiare');
  return response.json();
}

export async function createFamilyMember(data: { relation: string; birthYear?: number; conditions?: string[]; diagnosisAge?: number; notes?: string }): Promise<{ success: boolean; member: FamilyMember }> {
  const response = await fetch(`${API_BASE}/family`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nell\'aggiunta del familiare');
  return response.json();
}

export async function updateFamilyMember(id: number, data: Partial<FamilyMember>): Promise<{ success: boolean; member: FamilyMember }> {
  const response = await fetch(`${API_BASE}/family/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nell\'aggiornamento');
  return response.json();
}

export async function deleteFamilyMember(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/family/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}

// ==================== HEALTH NOTES ====================

export interface HealthNote {
  id: number;
  userId: string;
  title: string | null;
  content: string;
  type: string;
  tags: string[];
  date: string;
  createdAt: string;
  updatedAt: string;
}

export const noteTypes = [
  { value: 'general', label: 'Generale' },
  { value: 'symptom', label: 'Sintomo' },
  { value: 'appointment', label: 'Appuntamento' },
  { value: 'reminder', label: 'Promemoria' },
];

export async function getHealthNotes(params?: { search?: string; type?: string; limit?: number }): Promise<{ success: boolean; notes: HealthNote[] }> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  const query = searchParams.toString() ? `?${searchParams}` : '';

  const response = await fetch(`${API_BASE}/notes${query}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento delle note');
  return response.json();
}

export async function createHealthNote(data: { title?: string; content: string; type?: string; tags?: string[]; date?: string }): Promise<{ success: boolean; note: HealthNote }> {
  const response = await fetch(`${API_BASE}/notes`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nella creazione della nota');
  return response.json();
}

export async function updateHealthNote(id: number, data: Partial<HealthNote>): Promise<{ success: boolean; note: HealthNote }> {
  const response = await fetch(`${API_BASE}/notes/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nell\'aggiornamento');
  return response.json();
}

export async function deleteHealthNote(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/notes/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}

// ==================== MEDICATIONS ====================

export interface Medication {
  id: number;
  userId: string;
  name: string;
  type: string;
  dosage: string | null;
  frequency: string | null;
  reason: string | null;
  startDate: string;
  endDate: string | null;
  prescriber: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
}

export const medicationTypes = [
  { value: 'medication', label: 'Farmaco' },
  { value: 'supplement', label: 'Integratore' },
];

export async function getMedications(active?: boolean): Promise<{ success: boolean; medications: Medication[] }> {
  const params = active !== undefined ? `?active=${active}` : '';
  const response = await fetch(`${API_BASE}/medications${params}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento dei farmaci');
  return response.json();
}

export async function createMedication(data: {
  name: string;
  type?: string;
  dosage?: string;
  frequency?: string;
  reason?: string;
  startDate: string;
  endDate?: string;
  prescriber?: string;
  notes?: string;
}): Promise<{ success: boolean; medication: Medication }> {
  const response = await fetch(`${API_BASE}/medications`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nell\'aggiunta del farmaco');
  return response.json();
}

export async function updateMedication(id: number, data: Partial<Medication>): Promise<{ success: boolean; medication: Medication }> {
  const response = await fetch(`${API_BASE}/medications/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nell\'aggiornamento');
  return response.json();
}

export async function deleteMedication(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/medications/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}
